import { useEffect } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";

import { useQuery } from "@apollo/client";

import { QUERY_AUTHENTICATED_USER } from "api/queries/account";
import Footer from "components/Footer";
import Header from "components/Header";
import Loading from "components/Loading";
import PageDialogScreen from "components/Page/DialogScreen";
import Page404 from "pages/Error/404";
import AUTHENTICATED_ROUTES from "routes/authenticated";
import RouteRequireAuth from "routes/components/RouteRequireAuth";
import RouteTitle from "routes/components/RouteTitle";
import RouteUnauthenticated from "routes/components/RouteUnauthenticated";
import { RouteType } from "routes/types";
import UNAUTHENTICATED_ROUTES from "routes/unauthenticated";
import { APP_URLS } from "settings";
import { useUserStore } from "store/User";
import COMMON_ROUTES from "routes/common";

const renderRoute = (route: RouteType, i: number) => (
  <Route
    key={i}
    path={route.path}
    element={
      <RouteTitle title={route.title}>
        <route.component />
      </RouteTitle>
    }
  />
);

export default function App() {
  const setUser = useUserStore((state) => state.setUser);

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
  } = useQuery(QUERY_AUTHENTICATED_USER, {
    fetchPolicy: "no-cache",
  });

  /**
   * Check API response and set user state based on authenticated result
   */
  useEffect(() => {
    queryData?.broker && setUser(queryData.broker);
  }, [queryData, setUser]);

  useEffect(() => {
    if (queryData && !queryData.broker && queryError) {
      window.location.href = APP_URLS.LOGIN;
    }
  }, [queryData, queryError]);

  /**
   * Restore scroll window position on route change
   * @param props
   * @returns Route
   */
  const ScrollToTop = (props: any) => {
    const location = useLocation();
    useEffect(() => {
      window.scrollTo(0, 0);
    }, [location]);

    return <>{props.children}</>;
  };

  if (queryLoading) {
    return (
      <PageDialogScreen logo={false}>
        <Loading large />
      </PageDialogScreen>
    );
  }

  const isAuthenticated = queryData?.broker;
  const isAdmin = queryData?.broker?.isAdmin;
  const isBroker = queryData?.broker?.isBroker;

  return (
    <Router>
      <Header />
      <ScrollToTop>
        <Routes>
          <Route element={<RouteRequireAuth authenticated={isAuthenticated} />}>
            {AUTHENTICATED_ROUTES.COMMON_ROUTES.map(renderRoute)}

            {isAdmin && AUTHENTICATED_ROUTES.ADMIN_ROUTES.map(renderRoute)}

            {isBroker && AUTHENTICATED_ROUTES.BROKER_ROUTES.map(renderRoute)}
          </Route>
          <Route
            element={<RouteUnauthenticated authenticated={isAuthenticated} />}
          >
            {UNAUTHENTICATED_ROUTES.map(renderRoute)}
          </Route>
          {COMMON_ROUTES.map(renderRoute)}
          <Route
            path="*"
            element={
              <RouteTitle>
                <Page404 />
              </RouteTitle>
            }
          />
        </Routes>
      </ScrollToTop>
      <Footer />
    </Router>
  );
}
